import { useCallback, useState, useRef, useEffect } from 'react';
import { unselect } from '@clds/compound-components';
import { Separator } from '@cld/navigation-menu';
import { SearchBar } from './SearchBar/SearchBar';
import { shouldShowSearchBar } from './utils';
import { MenuSelectedId, WorkspaceMenuHookProp, WorkspaceMenuHookPropReturn } from './WorkspaceMenu.types';

export const useWorkspaceMenu = ({
  items,
  footerItem,
  showSearch,
  onSearchTermChange,
  defaultSelectedId = unselect,
  onSelect,
}: WorkspaceMenuHookProp): WorkspaceMenuHookPropReturn => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [withSearchBar, setWithSearchBar] = useState(showSearch);
  const showSearchBarTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const [selectedId, setSelectedId] = useState<MenuSelectedId>(defaultSelectedId);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleSelectChange = useCallback(
    (id: MenuSelectedId) => {
      onSelect?.(String(id));
      setSelectedId(id);
    },
    [onSelect, setSelectedId]
  );

  const handleSearchTermChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      onSearchTermChange?.(term);
    },
    [onSearchTermChange]
  );

  const onOpenChange = useCallback(
    (visible: boolean) => {
      if (showSearchBarTimeoutId.current) {
        clearTimeout(showSearchBarTimeoutId.current);
      }

      if (visible) {
        if (shouldShowSearchBar(items, showSearch)) {
          setWithSearchBar(true);
        }
      } else {
        handleSearchTermChange('');
        // we want to wait until the fade-out animation ends before hiding the search bar
        showSearchBarTimeoutId.current = setTimeout(() => setWithSearchBar(showSearch), 200);
      }
    },
    [handleSearchTermChange, items, showSearch]
  );

  const searchBar = withSearchBar && (
    <>
      <Separator />
      <SearchBar ref={searchRef} searchTerm={searchTerm} onChange={handleSearchTermChange} />
    </>
  );

  const footer = footerItem && (
    <>
      <Separator />
      {footerItem}
    </>
  );

  return {
    onOpenChange,
    footer,
    searchBar,
    selectedId,
    setSelectedId: handleSelectChange,
  };
};
